import { useQuery } from "convex/react";
import { useAtom, useSetAtom } from "jotai";
import { Building2, Globe2, LogOut, RefreshCw, Settings as SettingsIcon, User } from "@/shared/ui/icons";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { api } from "../../convex/_generated/api";
import { useActiveAccount } from "@/core/auth/activeAccount";
import { useSession } from "@/core/auth/useSession";
import { useIsMobile } from "@/core/hooks";
import { activeAccountScopeAtom, settingsModalOpenAtom } from "@/core/state";
import { Avatar, Button, Infobulle, useMenu, type EntreeMenu } from "@/shared/ui";
import { PanneauProfil } from "./PanneauProfil";

/**
 * Avatar + pseudo : badge utilisateur affiche dans la TitleBar (chrome haut)
 * a la place d'avoir une ligne dediee. Click → menu deroulant (`Menu` de la
 * librairie) avec "Ma photo et mon pseudo", "Settings" + "Sign out".
 *
 * Sources de verite :
 *   - `users.me()` Convex pour le pseudo (champ `name`, editable via Settings)
 *   - `useAuth()` WorkOS pour l'avatar (`profilePictureUrl` Google/etc.)
 *   - Fallback "user" si Convex n'a pas encore sync, fallback icone si pas d'avatar
 */
export function UserBadge() {
  const { t } = useTranslation();
  // « Ma photo et mon pseudo » — le panneau à droite (cf PanneauProfil).
  const [profilOuvert, setProfilOuvert] = useState(false);
  const setSettingsOpen = useSetAtom(settingsModalOpenAtom);
  const { user, signOut } = useSession();
  const isMobile = useIsMobile();
  const { ouvrirSous, ouvert, menu } = useMenu();
  // `useQuery` est safe a appeler meme sans auth (renvoie undefined). On le
  // garde monte plutot que de skip pour eviter de remonter le composant
  // quand l'user se loggue (pas de flash).
  const { pseudo, avatarUrl } = useMonProfil();
  const { account } = useActiveAccount();

  if (!user) return null;

  const entrees: EntreeMenu[] = [
    { label: "Ma photo et mon pseudo", icone: <User className="h-4 w-4" />, onClick: () => setProfilOuvert(true) },
    { label: t("shell.topBar.settings"), icone: <SettingsIcon className="h-4 w-4" />, onClick: () => setSettingsOpen(true) },
    "sep",
    { label: t("shell.topBar.signOut"), icone: <LogOut className="h-4 w-4" />, onClick: () => void signOut() },
  ];

  const bouton = (
    <Button
      variant={ouvert ? "secondary" : "ghost"}
      size={isMobile ? "lg" : "md"}
      className={isMobile ? "h-9" : undefined}
      aria-label={t("shell.topBar.userProfile")}
      aria-haspopup="menu"
      aria-expanded={ouvert}
      onClick={(e) => ouvrirSous(e.currentTarget, entrees, { alignement: "droite" })}
      icon={<PastilleProfil avatarUrl={avatarUrl} taille={isMobile ? 24 : 20} />}
    >
      <span className={isMobile ? "max-w-[6rem] truncate" : "max-w-[10rem] truncate"}>{pseudo}</span>
    </Button>
  );

  return (
    <>
      {isMobile ? bouton : <Infobulle texte={pseudo} cote="bas">{bouton}</Infobulle>}
      {menu}
      {profilOuvert && <PanneauProfil accountId={account?._id ?? null} fermer={() => setProfilOuvert(false)} />}
    </>
  );
}

/** La photo ronde de la personne connectée ; sans photo, l'icône d'un profil. */
function PastilleProfil({ avatarUrl, taille }: { avatarUrl: string | null; taille: number }) {
  return <Avatar src={avatarUrl} icone={<User className="h-3 w-3" />} taille={taille} />;
}

/**
 * Le pseudo et la photo de la personne connectée, tels que les conversations
 * les affichent. Une seule règle, lue par le badge du haut, le menu
 * Paramètres du rail et l'en-tête de Personnel.
 */
export function useMonProfil(): { pseudo: string; avatarUrl: string | null } {
  const { user } = useSession();
  const me = useQuery(api.users.me);
  const { account } = useActiveAccount();
  const discord = useQuery(api.members.myProfileGate, account ? { accountId: account._id } : "skip");
  const fallbackName = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() || user?.email || "user";
  const aChoisi = discord?.accueilTermine === true;
  const pseudo = (aChoisi ? me?.name : discord?.discordUsername) ?? me?.name ?? fallbackName;
  const avatarUrl =
    (me?.hasCustomAvatar ? me.avatarUrl : null) ?? discord?.discordAvatarUrl ?? me?.avatarUrl ?? user?.profilePictureUrl ?? null;
  return { pseudo, avatarUrl };
}

/**
 * Le rouage du rail (kiosque, sans barre du haut) : ce que portait le badge
 * profil de la barre du haut — qui je suis, Paramètres, mise à jour forcée,
 * déconnexion. Le menu s'ouvre vers la droite, à hauteur du rouage : il s'ancre sur une
 * balise invisible posée contre le bord droit du rail, le `Menu` le recale dans l'écran.
 */
export function MenuParametresDuRail() {
  const { t } = useTranslation();
  const setSettingsOpen = useSetAtom(settingsModalOpenAtom);
  const { user, signOut } = useSession();
  const { pseudo, avatarUrl } = useMonProfil();
  const { account } = useActiveAccount();
  const [profilOuvert, setProfilOuvert] = useState(false);
  const { ouvrirSous, ouvert, menu } = useMenu();
  const ancre = useRef<HTMLSpanElement>(null);
  // Le choix d'organisation, qui vivait dans la barre du haut
  // (`AccountScopeSwitcher`) : il ne sert qu'à qui en a plusieurs.
  const comptes = useQuery(api.accounts.listMine);
  const [portee, setPortee] = useAtom(activeAccountScopeAtom);

  async function forcerLaMiseAJour() {
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if ("caches" in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } finally {
      window.location.reload();
    }
  }

  if (!user) return null;

  const entrees: EntreeMenu[] = [
    // Qui je suis : la photo et le pseudo, et un clic ouvre « Ma photo et mon pseudo ».
    {
      label: pseudo,
      sous: "Ma photo et mon pseudo",
      icone: <PastilleProfil avatarUrl={avatarUrl} taille={24} />,
      onClick: () => setProfilOuvert(true),
    },
    ...(comptes && comptes.length > 1
      ? ([
          "sep",
          { titre: t("shell.accountScope.header") },
          {
            label: t("shell.accountScope.all"),
            icone: <Globe2 className="h-4 w-4" />,
            coche: portee?.kind === "all",
            onClick: () => setPortee({ kind: "all" }),
          },
          ...comptes.map(
            (c): EntreeMenu => ({
              label: c.name,
              icone: <Building2 className="h-4 w-4" />,
              coche:
                portee?.kind !== "all" && c._id === (portee?.kind === "single" ? portee.accountId : comptes[0]?._id),
              onClick: () => setPortee({ kind: "single", accountId: c._id }),
            }),
          ),
        ] satisfies EntreeMenu[])
      : []),
    "sep",
    { label: t("shell.topBar.settings"), icone: <SettingsIcon className="h-4 w-4" />, onClick: () => setSettingsOpen(true) },
    {
      label: t("shell.titleBar.hardRefresh", { defaultValue: "Forcer la mise à jour" }),
      icone: <RefreshCw className="h-4 w-4" />,
      onClick: () => void forcerLaMiseAJour(),
    },
    "sep",
    { label: t("shell.topBar.signOut"), icone: <LogOut className="h-4 w-4" />, onClick: () => void signOut() },
  ];

  return (
    <div className="relative">
      <Infobulle texte={t("shell.topBar.settings")} cote="droite">
        <Button
          variant={ouvert ? "secondary" : "ghost"}
          size="lg"
          iconOnly
          className="h-9 w-9"
          aria-label={t("shell.topBar.settings")}
          aria-haspopup="menu"
          aria-expanded={ouvert}
          onClick={() => ancre.current && ouvrirSous(ancre.current, entrees)}
        >
          <SettingsIcon className="h-[18px] w-[18px]" />
        </Button>
      </Infobulle>
      <span ref={ancre} aria-hidden className="pointer-events-none absolute left-full top-0 ml-3 h-full w-0" />
      {menu}
      {profilOuvert && <PanneauProfil accountId={account?._id ?? null} fermer={() => setProfilOuvert(false)} />}
    </div>
  );
}
